import { seatPriority } from "./formation.js";
import { MATCH_MODES, type MatchModeId } from "./mode.js";
import type { TeamSide } from "./types.js";

/**
 * Modos disponibles en online.
 *
 * Deliberadamente menos que los locales: cada modo online extra reparte a la
 * misma gente entre mas colas, y con base pequena eso se traduce en "no hay
 * nadie jugando". Los modos grandes viven en sala privada hasta que haya masa.
 */
export type OnlineModeId = "2v2" | "3v3" | "4v4" | "5v5" | "7v7";

export const ONLINE_MODE_IDS: readonly OnlineModeId[] = [
  "2v2",
  "3v3",
  "4v4",
  "5v5",
  "7v7",
];

export function isOnlineMode(id: MatchModeId): id is OnlineModeId {
  return (ONLINE_MODE_IDS as readonly string[]).includes(id);
}

/** Un asiento humano = jugador de campo. El portero NUNCA es asiento. */
export interface FieldSeat {
  /** Id de jugador en partida: `home-1`, `away-0`, ... */
  playerId: string;
  side: TeamSide;
  index: number;
  label: string;
}

/** Lista de asientos ocupables por humanos en un modo. */
export function fieldSeatsForMode(modeId: OnlineModeId): FieldSeat[] {
  const formation = MATCH_MODES[modeId].formation;
  const seats: FieldSeat[] = [];
  for (const side of ["home", "away"] as const) {
    formation.forEach((spec, index) => {
      if (spec.role === "gk") return;
      seats.push({
        playerId: `${side}-${index}`,
        side,
        index,
        label: spec.label ?? "FLD",
      });
    });
  }
  return seats;
}

/**
 * Asientos por orden de reparto: primero los mas ofensivos, y alternando lado
 * para que los equipos se llenen parejos.
 *
 * El reparto NO puede ser al azar. Medido con `pnpm ai:audit`: desde un medio
 * se tocaba el balon **0,48x** lo que lo tocaba un companero y no se tiraba ni
 * una vez por partido; desde la punta, 2,58x y 7 tiros. Con asientos al azar, la
 * primera partida de alguien nuevo era una moneda al aire entre divertirse y
 * aburrirse — y solo hay una primera partida.
 *
 * Los huecos que queden atras los cubren bots, que es justo donde menos se nota.
 */
export function seatDraftOrder(modeId: OnlineModeId): FieldSeat[] {
  const formation = MATCH_MODES[modeId].formation;
  const byIndex = new Map(fieldSeatsForMode(modeId).map((s) => [s.playerId, s]));
  const order: FieldSeat[] = [];
  for (const index of seatPriority(formation)) {
    for (const side of ["home", "away"] as const) {
      const seat = byIndex.get(`${side}-${index}`);
      if (seat) order.push(seat);
    }
  }
  return order;
}

export function maxHumanSlots(modeId: OnlineModeId): number {
  return fieldSeatsForMode(modeId).length;
}

/**
 * Ticks de margen entre el input enviado y el tick en que el server lo aplica.
 * Tiene que cubrir el viaje de ida completo: con 0 (valor original, pensado para
 * LAN) cualquier RTT > 16 ms hace que el input llegue a un tick ya emitido y se
 * sustituya por input neutro. El cliente sube este margen solo si el server le
 * avisa de llegadas tarde (`input:late`), y lo baja de nuevo cuando deja de pasar.
 */
export const LOCKSTEP_INPUT_DELAY = 6;
/**
 * Suelo del margen adaptativo: ~33 ms.
 *
 * Estaba en 4 (~66 ms) cuando la estimacion del reloj del servidor arrastraba
 * el jitter de cada bundle y hacia falta colchon. Con un ping de 12 ms eso era
 * retraso puro: se notaba entre pulsar y ver sin que hubiera lag ninguno.
 */
export const LOCKSTEP_MIN_LEAD = 2;
/** Techo del margen adaptativo: ~500 ms. Por encima el juego ya no responde. */
export const LOCKSTEP_MAX_LEAD = 30;
/**
 * Cuánto puede adelantarse un input al tick actual del server antes de tirarlo.
 * Sin este tope, un cliente manipulado llena `pending` con ticks futuros.
 */
export const LOCKSTEP_MAX_INPUT_LEAD = 240;
/** Cada cuántos ticks compara el cliente su estado con el resto (deteccion de desync). */
export const LOCKSTEP_HASH_INTERVAL = 60;
/**
 * Cuantos ticks seguidos puede rellenar el cliente con el ultimo input cuando el
 * margen sube de golpe. Sin tope, un salto grande del reloj estimado dispararia
 * cientos de mensajes; con el, el hueco se cubre y el resto queda en neutro.
 */
export const LOCKSTEP_MAX_FILL = 30;

/**
 * Cuanto tiempo sigue abierta una partida a quien llega tarde.
 *
 * Es la pieza que sostiene "siempre hay un partido en marcha": mientras la
 * puerta este abierta, el que pulsa JUGAR cae DENTRO del partido que ya se esta
 * jugando en vez de abrirse una sala al lado. Cerrarla pronto devuelve el
 * problema — con la ventana en 30 s, a partir del primer minuto el online
 * volvia a ser gente jugando sola en salas paralelas.
 *
 * El late-join reproduce el partido desde el tick 0 (mandar historial parcial no
 * es opcion: el que entra simularia un estado distinto al de todos), asi que el
 * limite es cuanto tarda esa simulacion y cuanto pesa el historial. **Medido con
 * `pnpm latejoin:cost`**, no estimado:
 *
 *   | ventana | historial | simular (7v7) |
 *   |--------:|----------:|--------------:|
 *   |    30 s |   0,47 MB |         29 ms |
 *   |    90 s |   1,40 MB |         74 ms |
 *   |   150 s |   2,33 MB |        ~110 ms|
 *   |   180 s |   2,80 MB |        132 ms |
 *
 * O sea que los 30 s originales eran cinco veces mas conservadores de lo que
 * hacia falta: venian de extrapolar a partidos de 10 minutos (~9 MB), y aqui un
 * partido dura 5.
 *
 * 150 s = la MITAD del partido. El techo no lo pone el coste sino el sentido:
 * entrar cuando quedan 30 segundos no es entrar a jugar.
 */
export const LATEJOIN_WINDOW_SECONDS = 150;
